// --- Dependencies -------------------------------------------------------------------------------
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type TyStatTileTone = "neutral" | "brand" | "good" | "warn" | "bad";

type Props = {
    label: string;
    /** Pre-formatted — the tile never decides how a number is spelled, so currency stays with the screen. */
    value: string;
    /** The one line of context under the number: what the figure is of, or what it is out of. */
    hint?: string;
    tone?: TyStatTileTone;
    /** Makes the whole tile the link to the section it summarises. */
    to?: string;
    /** A sparkline or meter drawn under the number. */
    children?: ReactNode;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * One number, said once, big. The dashboard's job is to be read at a glance from across a desk, and a
 * figure that needs a sentence around it has already failed that — so the tile gives the value the
 * whole line and pushes everything else to a hint underneath.
 *
 * A tile carrying `to` renders as a link rather than a card, because a number an admin wants to act on
 * should not need them to go and find the section it came from.
 */
export default function StatTile({ label, value, hint, tone = "neutral", to, children }: Props) {
    const body = (
        <>
            <p className="stat-tile-label">{label}</p>
            <p className="stat-tile-value">{value}</p>
            {hint && <p className="stat-tile-hint">{hint}</p>}
            {children && <div className="stat-tile-extra">{children}</div>}
        </>
    );

    if (to)
        return (
            <NavLink className="stat-tile stat-tile--link" data-tone={tone} to={to}>
                {body}
            </NavLink>
        );

    return (
        <div className="stat-tile" data-tone={tone}>
            {body}
        </div>
    );
}
